import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { DashboardActionContext, FooterActionConfig } from '../../industry-ui.model';

/**
 * FooterActionButtonsComponent
 * ─────────────────────────────────────────────────────────────────────────────
 * Renders the "function-buttons-section" footer action bar that was previously
 * inlined inside dashboard.component.html (lines 964-986).
 *
 * Inputs:
 *   • actions  — FooterActionConfig[] from industryConfig.footerActions
 *   • context  — DashboardActionContext (the DashboardComponent itself, passed
 *                as `this`). Used for label(), disabledWhen() and handler().
 *
 * The parent (DashboardComponent) keeps its own `onFooterAction()` method
 * commented out and instead delegates via this component.
 */
@Component({
  selector: 'app-footer-action-buttons',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './footer-action-buttons.component.html',
  styleUrls: ['./footer-action-buttons.component.scss'],
})
export class FooterActionButtonsComponent {
  private _actions: FooterActionConfig[] = [];

  /** Rendered in ascending `order` so the config, not array position, decides placement. */
  @Input()
  set actions(value: FooterActionConfig[]) {
    this._actions = [...(value || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  get actions(): FooterActionConfig[] {
    return this._actions;
  }
  @Input() context!: DashboardActionContext;

  /**
   * Mirrors the original onFooterAction() method from DashboardComponent:
   *   if (action.disabledWhen?.(this)) return;
   *   action.handler(this);
   */
  onActionClick(action: FooterActionConfig): void {
    if (action.disabledWhen?.(this.context)) return;
    action.handler(this.context);
  }

  /**
   * Returns the resolved label string for an action.
   * Called from the template to avoid expression-changed errors.
   */
  getLabel(action: FooterActionConfig): string {
    return action.label(this.context);
  }

  /**
   * Returns true if the given action should be visually disabled.
   */
  isDisabled(action: FooterActionConfig): boolean {
    return action.disabledWhen ? action.disabledWhen(this.context) : false;
  }
}
